import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Download, Trash2, Play, RotateCw, Upload, AlertCircle, ShieldAlert } from "lucide-react";

async function apiDownload(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename || "backup.zip";
  document.body.appendChild(a); a.click();
  setTimeout(() => { window.URL.revokeObjectURL(url); a.remove(); }, 200);
}

function fmtSize(n) {
  if (!n && n !== 0) return "—";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export default function BackupPage() {
  const [data, setData] = useState({ items: [], external_freshness: {} });
  const [busy, setBusy] = useState(false);
  const [showRestoreUpload, setShowRestoreUpload] = useState(false);
  const [showRestoreExisting, setShowRestoreExisting] = useState(null);

  const load = async () => { const r = await api.get("/backup"); setData(r.data); };
  useEffect(() => { load(); }, []);

  const createManual = async () => {
    if (!window.confirm("Avviare un backup manuale ora? L'operazione può richiedere alcuni secondi in base ai documenti.")) return;
    setBusy(true);
    try {
      const r = await api.post("/backup");
      toast.success(`Backup completato: ${r.data.filename} (${fmtSize(r.data.size_bytes)})`);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Errore backup");
    } finally { setBusy(false); }
  };

  const doDelete = async (b) => {
    if (!window.confirm(`Eliminare definitivamente ${b.filename}? Il file su disco verrà rimosso.`)) return;
    try { await api.delete(`/backup/${b.id}`); toast.success("Backup eliminato."); load(); } catch { /* toast */ }
  };

  const download = async (b) => {
    try {
      await apiDownload(`/backup/${b.id}/download`, b.filename);
      toast.success("Download completato — la data di ultima copia esterna è stata aggiornata.");
      load();
    } catch { /* toast */ }
  };

  const fr = data.external_freshness || {};

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <h1 className="ma-h1" data-testid="backup-title">Backup & Ripristino</h1>
          <div className="ma-sub">Backup completi (database + documenti + impostazioni + checksum). Nessun segreto incluso.</div>
        </div>
        <div className="ma-actions">
          <button className="ma-btn-secondary-sm" onClick={() => setShowRestoreUpload(true)} data-testid="btn-open-restore-upload">
            <Upload size={14}/> Ripristina da file
          </button>
          <button className="ma-btn-primary-sm" onClick={createManual} disabled={busy} data-testid="btn-create-backup">
            <Play size={14}/> {busy ? "Backup in corso…" : "Nuovo backup manuale"}
          </button>
        </div>
      </div>

      {fr.warning && (
        <div className="ma-warning" data-testid="backup-external-warning">
          <ShieldAlert size={16}/>
          <span>
            {fr.last_external_download_at
              ? `L'ultimo download esterno risale a ${fr.days_since} giorni fa. Scarica una copia in un luogo sicuro fuori dal server.`
              : "Nessun backup è mai stato scaricato dal server. È indispensabile conservare almeno una copia esterna."}
          </span>
        </div>
      )}

      <div className="ma-table-wrap">
        <table className="ma-table">
          <thead><tr>
            <th>File</th><th>Tipo</th><th>Stato</th><th>Dimensione</th>
            <th>Iniziato</th><th>Completato</th><th>Durata</th><th>Ultimo download esterno</th><th></th>
          </tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan="9" className="ma-empty">Nessun backup ancora presente.</td></tr>}
            {data.items.map(b => (
              <tr key={b.id} data-testid={`backup-row-${b.id}`}>
                <td className="ma-mono">{b.filename}</td>
                <td><span className="ma-pill">{b.backup_type === "manual" ? "Manuale" : b.backup_type === "automatic" ? "Automatico" : b.backup_type === "pre-restore" ? "Pre-ripristino" : b.backup_type}</span></td>
                <td>
                  {b.status === "ok" && <span className="ma-pill pill-ok">OK</span>}
                  {b.status === "running" && <span className="ma-pill pill-warn">In corso</span>}
                  {b.status === "failed" && <span className="ma-pill pill-err" title={b.error_message || ""}>Errore</span>}
                  {b.status === "interrupted_by_restore" && <span className="ma-pill pill-warn" title={b.error_message || ""}>Interrotto dal ripristino</span>}
                  {b.status === "interrupted_by_crash" && <span className="ma-pill pill-warn" title={b.error_message || ""}>Interrotto (riavvio)</span>}
                </td>
                <td>{fmtSize(b.size_bytes)}</td>
                <td>{b.started_at ? new Date(b.started_at).toLocaleString("it-IT") : "—"}</td>
                <td>{b.completed_at ? new Date(b.completed_at).toLocaleString("it-IT") : "—"}</td>
                <td>{b.duration_seconds ? `${b.duration_seconds}s` : "—"}</td>
                <td>{b.last_external_download_at ? new Date(b.last_external_download_at).toLocaleString("it-IT") : "—"}</td>
                <td className="ma-row-actions">
                  {b.status === "ok" && (
                    <>
                      <button className="ma-btn-link" title="Scarica" onClick={() => download(b)} data-testid={`btn-dl-backup-${b.id}`}><Download size={14}/></button>
                      <button className="ma-btn-link" title="Ripristina questo backup" onClick={() => setShowRestoreExisting(b)} data-testid={`btn-restore-backup-${b.id}`}><RotateCw size={14}/></button>
                    </>
                  )}
                  <button className="ma-btn-link ma-danger" title="Elimina" onClick={() => doDelete(b)} data-testid={`btn-del-backup-${b.id}`}><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showRestoreUpload && <RestoreModal mode="upload" onClose={() => setShowRestoreUpload(false)} onDone={() => { setShowRestoreUpload(false); load(); }} />}
      {showRestoreExisting && <RestoreModal mode="existing" backup={showRestoreExisting} onClose={() => setShowRestoreExisting(null)} onDone={() => { setShowRestoreExisting(null); load(); }} />}
    </div>
  );
}

function RestoreModal({ mode, backup, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [verify, setVerify] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const doVerify = async () => {
    if (!file) { setErr("Seleziona il file .zip"); return; }
    setErr(null); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/backup/verify", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVerify(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Verifica fallita");
    } finally { setBusy(false); }
  };

  const doRestore = async () => {
    if (confirmText !== "CONFERMO_RIPRISTINO") { setErr("Digita esattamente CONFERMO_RIPRISTINO per procedere"); return; }
    setBusy(true); setErr(null);
    try {
      if (mode === "upload") {
        const fd = new FormData();
        fd.append("file", file); fd.append("confirm", "CONFERMO_RIPRISTINO");
        await api.post("/backup/restore/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        const fd = new FormData(); fd.append("confirm", "CONFERMO_RIPRISTINO");
        await api.post(`/backup/restore/${backup.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Ripristino completato. Stati backup riconciliati automaticamente.");
      onDone();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Ripristino fallito");
    } finally { setBusy(false); }
  };

  return (
    <div className="ma-modal-backdrop" onClick={onClose}>
      <div className="ma-modal" onClick={e=>e.stopPropagation()}>
        <h3 className="ma-h3">Ripristino {mode === "upload" ? "da file caricato" : `da backup ${backup?.filename || ""}`}</h3>
        <div className="ma-warning">
          <AlertCircle size={16}/>
          <span>Il ripristino sovrascrive il database e la cartella documenti. Verrà eseguito un backup automatico pre-ripristino. L'app entra in modalità manutenzione durante l'operazione.</span>
        </div>
        {mode === "upload" && (
          <>
            <label className="ma-label">Archivio ZIP di backup MasiAdvice *</label>
            <input type="file" accept=".zip,application/zip" className="ma-input" onChange={e=>{ setFile(e.target.files?.[0]); setVerify(null); }} data-testid="restore-file"/>
            <div className="ma-form-actions">
              <button className="ma-btn-secondary" onClick={doVerify} disabled={busy || !file}>Verifica archivio</button>
            </div>
          </>
        )}
        {verify && (
          <div className="ma-panel">
            <div className="ma-panel-head">Manifest</div>
            <div className="ma-panel-body">
              <pre className="ma-code">{JSON.stringify(verify.manifest, null, 2)}</pre>
              <div className="ma-hint">Checksum verificati: {verify.checksums_count}</div>
            </div>
          </div>
        )}
        <label className="ma-label" style={{marginTop:12}}>Per confermare, digita <code className="ma-mono">CONFERMO_RIPRISTINO</code> qui sotto:</label>
        <input className="ma-input" value={confirmText} onChange={e=>setConfirmText(e.target.value)} data-testid="restore-confirm"/>
        {err && <div className="ma-error"><AlertCircle size={14}/> {typeof err === "string" ? err : "Errore"}</div>}
        <div className="ma-form-actions">
          <button className="ma-btn-secondary" onClick={onClose}>Annulla</button>
          <button className="ma-btn-danger-sm" onClick={doRestore} disabled={busy || confirmText !== "CONFERMO_RIPRISTINO" || (mode === "upload" && !verify)} data-testid="btn-do-restore">
            {busy ? "Ripristino in corso…" : "Ripristina definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}
